import {
  AtsCareerSiteBackgroundTypeEnum,
  type AtsCareerSiteConfig,
  AtsCareerSiteVacanciesListTypeEnum,
  AtsCareerSiteVisibilityEnum,
} from '@/generated/graphql/types';
import {
  getContrastBackgroundColor,
  getContrastButtonColor,
  getContrastListVacanciesColor,
} from '@/org/views/career-site/shared/componentes/utils';
import { CareerSiteHeader } from '@/shared/components/public-career-site/components/header';
import { useRef } from 'react';
import { PublicListVacancies } from './public-list-vacancies';

interface CareerSiteDetailsProps {
  careerSiteConfig?: AtsCareerSiteConfig;
}

export const CareerSiteDetails: React.FC<CareerSiteDetailsProps> = ({
  careerSiteConfig,
}) => {
  // Ref for the vacancies section
  const vacanciesRef = useRef<HTMLDivElement>(null);

  // Function to handle scroll to vacancies section
  const handleScrollToVacancies = () => {
    vacanciesRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const header = {
    className:
      'tw-px-4 sm:tw-px-8 md:tw-px-16 lg:tw-px-24 xl:tw-px-32 2xl:tw-px-[30rem]',
    logoEnabled: careerSiteConfig?.logo_enabled ?? true,
    backgroundType:
      careerSiteConfig?.background_type ??
      AtsCareerSiteBackgroundTypeEnum.CareerSiteBackgroundSolid,
    backgroundColor: {
      primaryColor: careerSiteConfig?.background_color ?? '#F5F0FF',
      secondaryColor: getContrastBackgroundColor(
        careerSiteConfig?.background_color ?? '#F5F0FF',
      ),
    },
    logoUrl: {
      id: careerSiteConfig?.logo_file_id ?? undefined,
      url:
        careerSiteConfig?.logo_file?.file?.absolute_url ??
        careerSiteConfig?.company_info?.logo ??
        undefined,
    },
    title: careerSiteConfig?.title ?? 'Portal name',
    descriptionEnabled: careerSiteConfig?.description_enabled ?? true,
    textColor: {
      primaryColor: careerSiteConfig?.title_color ?? '#0F1019',
      secondaryColor:
        careerSiteConfig?.title_color === '#0F1019' ? '#0F1019' : '#FFFFFF',
    },
    description: careerSiteConfig?.description ?? 'Portal description',
    buttonEnabled: careerSiteConfig?.button_enabled ?? true,
    buttonText: careerSiteConfig?.button_label ?? 'Portal button text',
    buttonColor: {
      primaryColor: careerSiteConfig?.button_color ?? '#4B22F4',
      secondaryColor: getContrastButtonColor(
        careerSiteConfig?.button_color ?? '#4B22F4',
      ),
    },
    backgroundImage: {
      id: careerSiteConfig?.background_image_file_id ?? undefined,
      url:
        careerSiteConfig?.background_image_file?.file?.absolute_url ??
        undefined,
    },
  };

  const vacancies = {
    title: careerSiteConfig?.vacancies_list_title ?? 'Vacancies',
    tableStyle:
      careerSiteConfig?.vacancies_list_style ??
      AtsCareerSiteVacanciesListTypeEnum.CareerSiteVacanciesListCard,
    color: {
      primaryColor: careerSiteConfig?.vacancies_list_color ?? '#4B22F4',
      secondaryColor: getContrastListVacanciesColor(
        careerSiteConfig?.vacancies_list_color ?? '#4B22F4',
      ),
    },
  };
  return (
    <div className="tw-w-full tw-flex tw-justify-center tw-h-full tw-overflow-auto tw-mb-4">
      <div className="tw-flex tw-flex-col tw-w-full">
        <CareerSiteHeader
          {...header}
          previewType={undefined}
          onClick={handleScrollToVacancies}
          className="tw-px-4 sm:tw-px-8 md:tw-px-16 lg:tw-px-24 xl:tw-px-32 2xl:tw-px-[30rem] tw-py-4.8"
        />
        <div ref={vacanciesRef} className="tw-w-full tw-h-auto">
          <PublicListVacancies
            title={vacancies.title}
            tableStyle={vacancies.tableStyle}
            color={vacancies.color}
            showInfo={true}
            previewType={undefined}
            className="tw-px-4 sm:tw-px-8 md:tw-px-16 lg:tw-px-24 xl:tw-px-32 2xl:tw-px-[30rem]"
            type={AtsCareerSiteVisibilityEnum.ExternalAccess}
          />
        </div>
      </div>
    </div>
  );
};
